import type { JsonObject, JsonValue } from 'type-fest';

import {
  prepareAndMakeRequest,
  type PrepareAndMakeRequestOptions,
  type PartViewerPageData,
  type PartBaqData,
  type NonPrimaryPartFileBaqData,
  type UpdatePartFileRequestBody,
  type UpdatePartForBaqRequestBody,
  type PartMasterData,
  type BulkPartFileGeometricAnalysisData,
  type PartFileGeometricAnalysisData,
  type PartFileStandaloneViewerData,
} from '@frontend/api';
import type { ViewerDataResponseJson } from '@frontend/partviewer';

export type CadTree = {
  fileType: string;
  filename: string;
  uuid: string;
  viewerBody: {
    children: JsonObject[];
    descendent_body_viewer_ids: string[];
    id: number;
    parentViewerId?: string | null;
    viewerId: string;
    viewerName: string;
    viewer_part: {
      id: number;
      partFile: {
        contains_virus?: boolean | null;
        fileType: string;
        filename: string;
        name: string;
        uuid: string;
      };
      type: string;
      viewerBodyViewerIds: string[];
    };
  };
};

const printPages = {
  update: (printPageId: string, data: JsonObject): Promise<JsonValue> =>
    prepareAndMakeRequest.patch<JsonValue>(`/parts/pages/${printPageId}/`, {
      body: data,
    }),
};

const bulkCadTree = {
  get: (
    partFileUuids: string[],
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ): Promise<CadTree[]> =>
    prepareAndMakeRequest.post<CadTree[]>(`/parts/files/bulk_cad_tree`, {
      ...fetchOptions,
      body: { uuids: partFileUuids },
    }),
};

const geometricAnalysisData = {
  get: (
    partFileUuid: string,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ): Promise<PartFileGeometricAnalysisData> =>
    prepareAndMakeRequest.get<PartFileGeometricAnalysisData>(
      `/parts/files/${partFileUuid}/geometric_analysis_data`,
      fetchOptions,
    ),
};

const bulkGeometricAnalysisData = {
  get: (
    partFileUuids: string[],
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ): Promise<BulkPartFileGeometricAnalysisData[]> =>
    prepareAndMakeRequest.post<BulkPartFileGeometricAnalysisData[]>(
      `/parts/files/bulk_geometric_analysis_data`,
      { ...fetchOptions, body: { uuids: partFileUuids } },
    ),
};

const viewerData = {
  get: (
    partFileUuid: string,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ): Promise<ViewerDataResponseJson> =>
    prepareAndMakeRequest.get<ViewerDataResponseJson>(
      `/parts/files/${partFileUuid}/viewer_data`,
      fetchOptions,
    ),
};

type SearchData = {
  entityClass: string;
  entityId: string;
  page: number;
  pageSize: number;
};

const parametricSearch = {
  search: (
    partFileUuid: string,
    data: SearchData,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ): Promise<JsonValue> =>
    prepareAndMakeRequest.post<JsonValue>(
      `/parts/files/${partFileUuid}/parametric_search`,
      { ...fetchOptions, body: data },
    ),
};

const fileViewer = {
  get: async (partFileUuid: string): Promise<PartFileStandaloneViewerData> =>
    prepareAndMakeRequest.get<PartFileStandaloneViewerData>(
      `/parts/files/${partFileUuid}/standalone_viewer_data`,
      {},
    ),
};

const partFiles = {
  bulkCadTree,
  fileViewer,
  geometricAnalysisData,
  bulkGeometricAnalysisData,
  parametricSearch,
  viewerData,

  update: (
    partFileUuid: string,
    data: UpdatePartFileRequestBody,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ): Promise<NonPrimaryPartFileBaqData> =>
    prepareAndMakeRequest.patch(`/parts/files/${partFileUuid}`, {
      ...fetchOptions,
      body: data,
    }),
};

type FacetedSearchData = {
  areaIncluded: boolean;
  areaMaxDeflection: number;
  edgeLengthIncluded: boolean;
  edgeLengthMaxDeflection: number;
  excludeExactMatches: boolean;
  massPropertiesIncluded: boolean;
  massPropertiesMaxDeflection: number;
  materialSearch: string;
  materialSearchIncluded: boolean;
  maxDimIncluded: boolean;
  maxDimMaxDeflection: number;
  medDimIncluded: boolean;
  medDimMaxDeflection: number;
  minDimIncluded: boolean;
  minDimMaxDeflection: number;
  volumeIncluded: boolean;
  volumeMaxDeflection: number;

  page: number;
  pageSize: number;
};

const facetedSearch = {
  search: (
    partUuid: string,
    data: FacetedSearchData,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.post(`/parts/${partUuid}/faceted_search`, {
      ...fetchOptions,
      body: data,
    }),
};

const facetedSearchFilters = {
  create: (data: JsonObject, fetchOptions: PrepareAndMakeRequestOptions = {}) =>
    prepareAndMakeRequest.post(`/suppliers/faceted_search_filters`, {
      ...fetchOptions,
      body: data,
    }),

  list: (fetchOptions: PrepareAndMakeRequestOptions = {}) =>
    prepareAndMakeRequest.get(
      `/suppliers/faceted_search_filters`,
      fetchOptions,
    ),

  bulkDelete: (
    data: JsonValue[],
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.delete(
      `/suppliers/faceted_search_filters/bulk_delete`,
      { ...fetchOptions, body: data },
    ),
};

export default {
  facetedSearch,
  facetedSearchFilters,
  partFiles,
  printPages,

  bulkUpdate: (
    groupId: string,
    data: JsonObject,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.put(`/parts/${groupId}/bulk_update`, {
      ...fetchOptions,
      body: data,
    }),

  bulkPurge: (
    groupId: string,
    data: JsonObject,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.delete(`/parts/${groupId}/bulk_purge`, {
      ...fetchOptions,
      body: data,
    }),

  checkStatuses: (
    uuids: string[],
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.post('/parts/statuses', {
      ...fetchOptions,
      body: { uuids },
    }),

  getForBAQ: (
    partUuid: string,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ): Promise<PartBaqData> =>
    prepareAndMakeRequest.get(`/parts/${partUuid}/baq`, fetchOptions),

  updateForBAQ: (
    partUuid: string,
    data: UpdatePartForBaqRequestBody,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ): Promise<PartBaqData> =>
    prepareAndMakeRequest.patch(`/parts/${partUuid}/baq`, {
      ...fetchOptions,
      body: data,
    }),

  getForViewerPage: (
    { partUuid }: { partUuid: string },
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ): Promise<PartViewerPageData> =>
    prepareAndMakeRequest.get(`/parts/${partUuid}/`, {
      ...fetchOptions,
    }),

  updateForViewerPage: (
    {
      partUuid,
      data,
    }: {
      partUuid: string;
      data: {
        partNumber?: string | null;
        processTag?: string | null;
        revision?: string | null;
        description?: string | null;
        materialId?: number | null;
      };
    },
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ): Promise<PartViewerPageData> =>
    prepareAndMakeRequest.patch(`/parts/${partUuid}/`, {
      ...fetchOptions,
      body: data,
    }),

  updatePartMaster: (
    {
      partMasterUuid,
      data,
    }: { partMasterUuid: string; data: { isExportControlled: boolean } },
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ): Promise<PartMasterData> =>
    prepareAndMakeRequest.patch(`/parts/part_master/${partMasterUuid}/`, {
      ...fetchOptions,
      body: data,
    }),

  downloadTechnicalDataPackage: (
    partId: string,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.get(
      `/parts/${partId}/download_technical_data_package`,
      fetchOptions,
    ),

  attachFile: (
    partId: string,
    formData: FormData,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ): Promise<PartBaqData> =>
    prepareAndMakeRequest.post(`/parts/${partId}/files`, {
      ...fetchOptions,
      body: formData,
    }),

  removeAttachedFile: (
    fileId: string,
    data: { partId: string },
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.delete(`/parts/files/${fileId}`, {
      ...fetchOptions,
      body: data,
    }),

  mergeAsSupportingFiles: (
    parentUuid: string,
    childUuids: string[],
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.post(`/parts/${parentUuid}/merge_supporting_files/`, {
      ...fetchOptions,
      body: { childUuids },
    }),

  postVectorFileData: (
    partFileUuid: string,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.post(
      `/parts/vector_file/${partFileUuid}`,
      fetchOptions,
    ),

  getVectorFileData: (
    partUuid: string,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.get(
      `/parts/vector_file/${partUuid}/detail`,
      fetchOptions,
    ),

  editContourRequest: (
    partFileUuid: string,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.post(
      `/parts/files/${partFileUuid}/edit_contour`,
      fetchOptions,
    ),

  getContourTracker: (
    contourTrackerId: string,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.get(
      `/parts/edit_contour/${contourTrackerId}`,
      fetchOptions,
    ),

  updateContourRequest: (
    contourTrackerId: string,
    data: JsonObject,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.post(
      `/parts/edit_contour/${contourTrackerId}/update`,
      { ...fetchOptions, body: data },
    ),

  updateThicknessRequest: (
    partFileUuid: string,
    data: JsonObject,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.post(
      `/parts/files/${partFileUuid}/update_thickness/`,
      { ...fetchOptions, body: data },
    ),

  pageVectorizationRequest: (
    printPageId: string,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.post(
      `/parts/pages/${printPageId}/vectorization`,
      fetchOptions,
    ),

  getPageVectorization: (
    pageVectorizationId: string,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.get(
      `/parts/vectorization/${pageVectorizationId}`,
      fetchOptions,
    ),

  updatePageVectorization: (
    pageVectorizationId: string,
    data: JsonObject,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.patch(`/parts/vectorization/${pageVectorizationId}`, {
      ...fetchOptions,
      body: data,
    }),

  createDxfFileFromPageVectorization: (
    pageVectorizationId: string,
    data: JsonObject,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.post(
      `/parts/vectorization/${pageVectorizationId}/create_file`,
      { ...fetchOptions, body: data },
    ),

  switchPrimaryFile: (
    partUuid: string,
    data: JsonObject,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.post(`/parts/${partUuid}/switch_primary_file/`, {
      ...fetchOptions,
      body: data,
    }),

  restructureBom: (
    partUuid: string,
    data: JsonObject,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.post(`/parts/${partUuid}/bom_restructure`, {
      ...fetchOptions,
      body: data,
    }),

  similaritySearch: (
    partUuid: string,
    data: JsonObject,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.post(`/parts/${partUuid}/similarity_search`, {
      ...fetchOptions,
      body: data,
    }),

  getJobs: (
    partUuid: string,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) => prepareAndMakeRequest.get(`/parts/${partUuid}/jobs`, fetchOptions),

  getQuotes: (
    partUuid: string,
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) => prepareAndMakeRequest.get(`/parts/${partUuid}/quotes`, fetchOptions),

  getMultiLevelBom: (
    uuid: string,
    queryParams: { version: string },
    fetchOptions: PrepareAndMakeRequestOptions = {},
  ) =>
    prepareAndMakeRequest.get(`/parts/part_master/${uuid}/multi_level_bom`, {
      ...fetchOptions,
      query: queryParams,
    }),
};
